import { HeapPHIDecorator } from "@app/components/analytics";
import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  ClearErrors,
  ClearValidationError,
  SetValidationError,
} from "@natera/platform/lib/hooks/useError";
import { Button, Form } from "@natera/form";
import {
  ConfigContext,
  ErrorContext,
  IntlContext,
  Language,
  PasswordPolicyContext,
} from "@app/provider";
import {
  DropdownWrapper,
  FormField,
  PasswordField,
  PasswordNotice,
  usePasswordPolicy,
  EmailInputWithDomainSuggestions,
} from "@app/components";
import { validateEmail } from "@app/utils";
import { isPasswordValid } from "@app/utils/validatePassword";

import "./signUpByPasswordForm.scss";

const messages = defineMessages({
  signUpByPasswordFormEmailAddress: {
    id: "signUpByPasswordFormEmailAddress",
    defaultMessage: "Email Address",
  },
  signUpByPasswordFormFillFieldError: {
    id: "signUpByPasswordFormFillFieldError",
    defaultMessage: "Please fill in this field",
  },
  signUpByPasswordFormPasswordLabel: {
    id: "signUpByPasswordFormPasswordLabel",
    defaultMessage: "Password",
  },
  signUpByPasswordFormContinueButton: {
    id: "signUpByPasswordFormContinueButton",
    defaultMessage: "Continue",
  },
  signUpByPasswordFormCreateAccount: {
    id: "signUpByPasswordFormCreateAccount",
    defaultMessage: "Create Account",
  },
  signUpByPasswordFormEmailAddressPlaceholder: {
    id: "signUpByPasswordFormEmailAddressPlaceholder",
    defaultMessage: "Create Account with Email Address",
  },
  signUpByPasswordFormPasswordPlaceholder: {
    id: "signUpByPasswordFormPasswordPlaceholder",
    defaultMessage: "Create Password",
  },
  signUpByPasswordFormEmailInvalid: {
    id: "signUpByPasswordFormEmailInvalid",
    defaultMessage: "Please enter a valid email address. (username@domain)",
  },
  signUpByPasswordFormPasswordInvalid: {
    id: "signUpByPasswordFormPasswordInvalid",
    defaultMessage: "Invalid password",
  },
  signUpByPasswordFormEmailFormat: {
    id: "signUpByPasswordFormEmailFormat",
    defaultMessage: "Email format (username@domain)",
  },
  signUpByPasswordFormSignInTitle: {
    id: "signUpByPasswordFormSignInTitle",
    defaultMessage: "Log In",
  },
});

interface SignUpByPasswordFormProps {
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;
  clearErrors: ClearErrors;
  onSubmit: (props: {
    email?: string;
    password?: string;
    language: Language;
  }) => void;
  initialEmail?: string;
  initialPassword?: string;
}
const SignUpByPasswordForm: FC<SignUpByPasswordFormProps> = ({
  setValidationError,
  clearValidationError,
  clearErrors,
  onSubmit,
  initialEmail,
  initialPassword,
}) => {
  const intl = useIntl();

  const { config } = React.useContext(ConfigContext);
  const { currentLanguage } = useContext(IntlContext);
  const { getValidationError } = useContext(ErrorContext);
  const { getPasswordPolicy } = useContext(PasswordPolicyContext);

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const [passwordNoticeIsVisible, setPasswordNoticeIsVisible] = useState(false);

  const [email, setEmail] = useState(initialEmail ?? "");
  const [password, setPassword] = useState(initialPassword ?? "");
  const { passwordPolicyComplexity: passwordPolicyComplexityValidationState } =
    usePasswordPolicy({ email, password, currentLanguage });
  const [submitIsDisabled, setSubmitIsDisabled] = useState(false);
  const [emailTooltipIsOpened, setEmailTooltipIsOpened] = useState(false);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  useEffect(() => {
    if (initialPassword) {
      setPassword(initialPassword);
    }
  }, [initialPassword]);

  useEffect(() => {
    getPasswordPolicy();

    return () => {
      clearErrors();
    };
  }, []);

  const isPasswordValidCheck = (pwd?: string) => {
    if (!pwd) {
      setValidationError(
        "password",
        intl.formatMessage(messages.signUpByPasswordFormFillFieldError)
      );
      return false;
    }
    const invalid = !isPasswordValid({
      password: pwd,
      passwordPolicyComplexity: passwordPolicyComplexityValidationState,
    });
    if (invalid) {
      setValidationError(
        "password",
        intl.formatMessage(messages.signUpByPasswordFormPasswordInvalid)
      );
      return false;
    }
    return true;
  };

  const isEmailValidCheck = (email?: string) => {
    if (!email) {
      setValidationError(
        "email",
        intl.formatMessage(messages.signUpByPasswordFormFillFieldError)
      );
      return false;
    }

    const ok = validateEmail(email.trim(), usePlusSignInEmailAddress);
    if (!ok) {
      setValidationError(
        "email",
        intl.formatMessage(messages.signUpByPasswordFormEmailInvalid)
      );
      return false;
    }
    return true;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void submitImpl(e.currentTarget);
  };

  function submitImpl(form: HTMLFormElement) {
    clearErrors();

    const formData = new FormData(form);
    const emailValue = email.trim();
    const password = (formData.get("password") as string) ?? "";

    const isValidEmail = isEmailValidCheck(email);
    const isValidPassword = isPasswordValidCheck(password);

    if (isValidEmail && isValidPassword) {
      onSubmit({ email: emailValue, password, language: currentLanguage });
    }
  }

  const handleEmailFieldFocus = () => {
    setEmailTooltipIsOpened(true);
  };

  const handleEmailFieldBlur = () => {
    setEmailTooltipIsOpened(false);
  };

  const handlePasswordFieldFocus = () => {
    setPasswordNoticeIsVisible(true);
  };

  const handlePasswordFieldBlur = () => {
    setPasswordNoticeIsVisible(false);
  };

  const handleEmailFieldChange = (value: string) => {
    setEmail(value);
    clearValidationError("email");

    if (!value) {
      clearValidationError("email");
      setSubmitIsDisabled(false);
      return;
    }
  };

  const handlePasswordFieldChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    setPassword(e.currentTarget.value);
  };

  const emailAddressFormFieldElement: ReactElement = (
    <FormField
      label={intl.formatMessage(messages.signUpByPasswordFormEmailAddress)}
      required={true}
      error={getValidationError("email")}
      htmlFor="email"
      withPadding
    >
      <HeapPHIDecorator protectAttr={["value"]} noWrap>
        <EmailInputWithDomainSuggestions
          placeholder={intl.formatMessage(
            messages.signUpByPasswordFormEmailAddressPlaceholder
          )}
          outline
          id="email"
          name="email"
          required
          onFocus={handleEmailFieldFocus}
          onBlur={handleEmailFieldBlur}
          onChange={handleEmailFieldChange}
          autoComplete="email"
          value={email}
          tooltipContent={intl.formatMessage(
            messages.signUpByPasswordFormEmailFormat
          )}
          showTooltip={emailTooltipIsOpened}
          error={getValidationError("email")}
        />
      </HeapPHIDecorator>
    </FormField>
  );

  const passwordFormFieldElement: ReactElement = (
    <FormField
      label={intl.formatMessage(messages.signUpByPasswordFormPasswordLabel)}
      required={true}
      error={getValidationError("password")}
      htmlFor="password"
      withPadding
    >
      <HeapPHIDecorator protectAttr={["value"]} noWrap>
        <PasswordField
          placeholder={intl.formatMessage(
            messages.signUpByPasswordFormPasswordPlaceholder
          )}
          outline={true}
          id="password"
          name="password"
          required={true}
          defaultValue={initialPassword}
          onFocus={handlePasswordFieldFocus}
          onBlur={handlePasswordFieldBlur}
          onChange={handlePasswordFieldChange}
          autoComplete="new-password"
        />
      </HeapPHIDecorator>
    </FormField>
  );

  const passwordNoticeElement: ReactElement = (
    <DropdownWrapper isOpen={passwordNoticeIsVisible}>
      <PasswordNotice
        passwordPolicyComplexity={passwordPolicyComplexityValidationState}
      />
    </DropdownWrapper>
  );

  const renderField = () => (
    <>
      {emailAddressFormFieldElement}
      {passwordFormFieldElement}
      {passwordNoticeElement}
    </>
  );

  return (
    <Form
      className="sign-up-by-password-form"
      onSubmit={handleSubmit}
      noValidate
    >
      {renderField()}
      <Button type="submit" disabled={submitIsDisabled} unelevated>
        {intl.formatMessage(messages.signUpByPasswordFormCreateAccount)}
      </Button>
    </Form>
  );
};

export default SignUpByPasswordForm;
